import { AggregationType, type DataRow, RowType } from "./table.types";
import { checkNever } from "portal/utils/identity";
import { isBlankValue } from "portal/utils/reports";
import { Measurement } from "../../measurement/Measurement";
import { MetricEnum, MetricNumber, MetricString } from "portal/utils/metrics";
import { useTranslation } from "react-i18next";
import React, { type FunctionComponent, type ReactNode } from "react";

type CellMetric = MetricEnum | MetricNumber | MetricString;

type DataCellValue = ReturnType<CellMetric["getValue"]>;

/**
 * Renders one certified metric cell in the reports grid: a single day/job value,
 * stacked total+average for a robot group row, or total vs average on the
 * bottom-line rows depending on `showTotals` / `showAverages`.
 */
export const CarbonDataCell: FunctionComponent<{
  metric: CellMetric;
  row: DataRow;
  showTotals: boolean;
  showAverages: boolean;
}> = ({ metric, row, showTotals, showAverages }) => {
  const { t } = useTranslation();
  const key = metric.id;

  // Render a measurement component for this met, after filtering out blank
  // values.
  const measurement = (values: DataCellValue | DataCellValue[]): ReactNode => {
    const unit = "units" in metric ? metric.units : undefined;
    const one = (value: DataCellValue, key?: number): ReactNode => {
      if (metric.type === "enum") {
        // Aggregates widen the cell value; only a number can be an enum.
        const label = metric.formatValue(
          typeof value === "number" ? value : undefined,
          t
        );
        // Undefined is an empty cell. Measurement would draw an em dash.
        return <span key={key}>{label ?? ""}</span>;
      }
      return (
        <Measurement
          key={key}
          cycleSlot={metric.cycleSlot}
          unit={unit}
          value={isBlankValue(value) ? undefined : value}
        />
      );
    };
    return (
      <div className="flex flex-col h-full items-end justify-around leading-none w-full">
        {Array.isArray(values)
          ? values.map((value, i) => one(value, i))
          : one(values)}
      </div>
    );
  };

  const unanimousOrMixed = (unanimous: DataCellValue): ReactNode => {
    const isUnanimous = !isBlankValue(unanimous);
    // Categorical aggregate: not unanimous across rows — show “mixed” label.
    return isUnanimous ? (
      measurement(unanimous)
    ) : (
      <span className="opacity-50 italic w-full text-end">
        {t("views.reports.scheduled.table.fields.mixed")}
      </span>
    );
  };

  switch (row.type) {
    case RowType.SINGLE_JOB:
    case RowType.SINGLE_DAY: {
      return measurement(row.metrics[key]);
    }

    case RowType.ROBOT_AGGREGATES: {
      const { aggregates } = row;
      const total = aggregates.totals[key];
      const average = aggregates.averages[key];
      if (!metric.canTotal && !metric.canAverage) {
        return showAverages
          ? unanimousOrMixed(aggregates.unanimous[key])
          : measurement(undefined);
      }
      const values = [];
      if (showTotals) {
        values.push(total);
      }
      if (showAverages) {
        values.push(average);
      }
      return measurement(values);
    }

    case RowType.BOTTOM_LINE_AGGREGATES: {
      const { aggregationType, aggregates } = row;
      // this is clearer as a switch, even though it's nested
      // eslint-disable-next-line sonarjs/no-nested-switch
      switch (aggregationType) {
        case AggregationType.TOTAL: {
          return measurement(aggregates.totals[key]);
        }
        case AggregationType.AVERAGE: {
          return metric.canAverage
            ? measurement(aggregates.averages[key])
            : unanimousOrMixed(aggregates.unanimous[key]);
        }
        default: {
          console.error(
            "Unexpected aggregation type in data cell:",
            checkNever(aggregationType)
          );
          return measurement(undefined);
        }
      }
    }

    default: {
      console.error("Unexpected row type in data cell:", checkNever(row));
      return measurement(undefined);
    }
  }
};
